/**
 * DV-STAGE-001: Run Controls Component
 * Controls for stage execution with overrides, sandbox mode, and status display
 */

import { useState, useCallback } from 'react';
import { Play, Square, RotateCcw, Settings, Save, Shield, AlertTriangle } from 'lucide-react';
import { cn } from '@/utils/cn';
import { type LogEntry } from './LogsPanel';
import { type StageId } from './StageCard';

export interface StageOverrides {
  provider: 'openai' | 'anthropic' | 'google';
  model: string;
  temperature: number;
  topP: number;
  seed: number | null;
  tools: boolean;
  maxCompletionTokens?: number; // For GPT-5 max_completion_tokens
}

export interface RunResult {
  timestamp: string;
  success: boolean;
  duration: number; // ms
  cost: number; // USD
  tokens: { input: number; output: number };
  requestId?: string;
  error?: string;
  output?: any; // The actual stage output
}

export interface RunControlsProps {
  stageId: StageId;
  overrides: StageOverrides;
  sandboxMode: boolean;
  saveToExecution: boolean;
  isRunning: boolean;
  lastRun: RunResult | null;
  onOverridesChange: (overrides: StageOverrides) => void;
  onModeChange: (sandboxMode: boolean, saveToExecution: boolean) => void;
  onRun: () => void;
  onCancel: () => void;
  onLog: (entry: Omit<LogEntry, 'id'>) => void;
  className?: string;
  'data-testid'?: string;
}

// Model options by provider - Updated with latest models
const MODEL_OPTIONS: Record<StageOverrides['provider'], string[]> = {
  openai: ['gpt-5', 'gpt-5-mini', 'gpt-4o'],
  anthropic: ['claude-opus-4-1-20250805', 'claude-sonnet-4-20250514', 'claude-3-5-sonnet-20241022', 'claude-3-7-sonnet-20250219'],
  google: ['gemini-2.5-flash', 'gemini-2.5-pro'],
};

// Preset configurations
const PRESETS: Array<{ name: string; overrides: Partial<StageOverrides> }> = [
  { name: 'Balanced', overrides: { temperature: 0.7, topP: 1.0, tools: true } },
  { name: 'Creative', overrides: { temperature: 0.9, topP: 0.95, tools: true } },
  { name: 'Precise', overrides: { temperature: 0.3, topP: 0.8, tools: false } },
  { name: 'Deterministic', overrides: { temperature: 0.0, topP: 1.0, seed: 12345, tools: false } },
];

export function RunControls({
  stageId,
  overrides,
  sandboxMode,
  saveToExecution,
  isRunning,
  lastRun,
  onOverridesChange,
  onModeChange,
  onRun,
  onCancel,
  onLog,
  className,
  'data-testid': testId = `run-controls-${stageId}`
}: RunControlsProps) {
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Handle provider change
  const handleProviderChange = useCallback((provider: StageOverrides['provider']) => {
    const newModel = MODEL_OPTIONS[provider][0];
    const newOverrides = { ...overrides, provider, model: newModel };
    onOverridesChange(newOverrides);

    onLog({
      ts: new Date().toISOString(),
      level: 'debug',
      text: `Provider changed to ${provider} for ${stageId}`,
      meta: {
        stage: stageId,
        provider,
        model: newModel,
        source: 'run_controls',
      }
    });
  }, [stageId, overrides, onOverridesChange, onLog]);

  // Handle model change
  const handleModelChange = useCallback((model: string) => {
    const newOverrides = { ...overrides, model };
    onOverridesChange(newOverrides);

    onLog({
      ts: new Date().toISOString(),
      level: 'debug',
      text: `Model changed to ${model} for ${stageId}`,
      meta: {
        stage: stageId,
        provider: overrides.provider,
        model,
        source: 'run_controls',
      }
    });
  }, [stageId, overrides, onOverridesChange, onLog]);

  // Apply preset
  const applyPreset = useCallback((preset: typeof PRESETS[0]) => {
    const newOverrides = { ...overrides, ...preset.overrides };
    onOverridesChange(newOverrides);

    onLog({
      ts: new Date().toISOString(),
      level: 'info',
      text: `Applied ${preset.name} preset for ${stageId}`,
      meta: {
        stage: stageId,
        presetName: preset.name,
        overrides: preset.overrides,
        source: 'run_controls',
      }
    });
  }, [stageId, overrides, onOverridesChange, onLog]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    const defaultOverrides: StageOverrides = {
      provider: 'openai',
      model: 'gpt-5',
      temperature: 1.0, // GPT-5 uses temperature of 1
      topP: 1.0,
      seed: null,
      tools: true,
      maxCompletionTokens: 4096, // GPT-5 default max_completion_tokens
    };
    onOverridesChange(defaultOverrides);

    onLog({
      ts: new Date().toISOString(),
      level: 'info',
      text: `Reset overrides to defaults for ${stageId}`,
      meta: {
        stage: stageId,
        source: 'run_controls',
      }
    });
  }, [stageId, onOverridesChange, onLog]);

  // Handle sandbox mode toggle
  const handleSandboxToggle = useCallback(() => {
    const newSandboxMode = !sandboxMode;
    const newSaveToExecution = newSandboxMode ? false : saveToExecution;
    onModeChange(newSandboxMode, newSaveToExecution);

    onLog({
      ts: new Date().toISOString(),
      level: 'info',
      text: `Sandbox mode ${newSandboxMode ? 'enabled' : 'disabled'} for ${stageId}`,
      meta: {
        stage: stageId,
        sandboxMode: newSandboxMode,
        saveToExecution: newSaveToExecution,
        source: 'run_controls',
      }
    });
  }, [stageId, sandboxMode, saveToExecution, onModeChange, onLog]);

  // Handle save to execution toggle
  const handleSaveToggle = useCallback(() => {
    if (sandboxMode) return; // Can't save when in sandbox mode

    const newSaveToExecution = !saveToExecution;
    onModeChange(sandboxMode, newSaveToExecution);

    onLog({
      ts: new Date().toISOString(),
      level: 'info',
      text: `Save to execution ${newSaveToExecution ? 'enabled' : 'disabled'} for ${stageId}`,
      meta: {
        stage: stageId,
        saveToExecution: newSaveToExecution,
        source: 'run_controls',
      }
    });
  }, [stageId, sandboxMode, saveToExecution, onModeChange, onLog]);

  // Format duration for display
  const formatDuration = (ms: number): string => {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  return (
    <div className={cn("space-y-4", className)} data-testid={testId}>
      {/* Overrides Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h5
            className="text-sm font-mono font-medium"
            style={{
              color: 'var(--fg-primary)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            Overrides
          </h5>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
              data-testid={`${testId}-advanced-toggle`}
            >
              <Settings className="w-3 h-3" />
              {showAdvanced ? 'Basic' : 'Advanced'}
            </button>
            <button
              onClick={resetToDefaults}
              className="flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
              data-testid={`${testId}-reset`}
            >
              <RotateCcw className="w-3 h-3" />
              Reset
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Provider */}
          <div>
            <label
              className="block text-xs font-mono mb-1"
              style={{
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              Provider
            </label>
            <select
              value={overrides.provider}
              onChange={(e) => handleProviderChange(e.target.value as StageOverrides['provider'])}
              className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
              data-testid={`${testId}-provider`}
            >
              <option value="openai">OpenAI</option>
              <option value="anthropic">Anthropic</option>
              <option value="google">Google</option>
            </select>
          </div>

          {/* Model */}
          <div>
            <label
              className="block text-xs font-mono mb-1"
              style={{
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-family-mono)',
              }}
            >
              Model
            </label>
            <select
              value={overrides.model}
              onChange={(e) => handleModelChange(e.target.value)}
              className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--bg-surface)',
                borderColor: 'var(--border-default)',
                color: 'var(--fg-primary)',
                fontFamily: 'var(--font-family-mono)',
              }}
              data-testid={`${testId}-model`}
            >
              {MODEL_OPTIONS[overrides.provider].map(model => (
                <option key={model} value={model}>{model}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Advanced Parameters */}
        {showAdvanced && (
          <div className="mt-4 grid grid-cols-2 gap-4">
            {/* Temperature */}
            <div>
              <label
                className="block text-xs font-mono mb-1"
                style={{
                  color: 'var(--fg-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Temperature: {overrides.temperature}
              </label>
              <input
                type="range"
                min="0"
                max="2"
                step="0.1"
                value={overrides.temperature}
                onChange={(e) => onOverridesChange({ ...overrides, temperature: parseFloat(e.target.value) })}
                className="w-full"
                data-testid={`${testId}-temperature`}
              />
            </div>

            {/* Top P */}
            <div>
              <label
                className="block text-xs font-mono mb-1"
                style={{
                  color: 'var(--fg-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Top P: {overrides.topP}
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={overrides.topP}
                onChange={(e) => onOverridesChange({ ...overrides, topP: parseFloat(e.target.value) })}
                className="w-full"
                data-testid={`${testId}-top-p`}
              />
            </div>

            {/* Seed */}
            <div>
              <label
                className="block text-xs font-mono mb-1"
                style={{
                  color: 'var(--fg-muted)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Seed (optional)
              </label>
              <input
                type="number"
                value={overrides.seed || ''}
                onChange={(e) => onOverridesChange({ ...overrides, seed: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Random"
                className="w-full px-3 py-1 text-sm border rounded focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
                data-testid={`${testId}-seed`}
              />
            </div>

            {/* Max Completion Tokens (for GPT-5) */}
            {overrides.provider === 'openai' && overrides.model.startsWith('gpt-5') && (
              <div>
                <label
                  className="block text-xs font-mono mb-1"
                  style={{
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  Max Completion Tokens
                </label>
                <input
                  type="number"
                  value={overrides.maxCompletionTokens || 4096}
                  onChange={(e) => onOverridesChange({ ...overrides, maxCompletionTokens: e.target.value ? parseInt(e.target.value) : undefined })}
                  placeholder="4096"
                  min="1"
                  max="32768"
                  className="w-full px-3 py-1 text-sm border rounded focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: 'var(--bg-surface)',
                    borderColor: 'var(--border-default)',
                    color: 'var(--fg-primary)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                  data-testid={`${testId}-max-tokens`}
                />
              </div>
            )}

            {/* Tools */}
            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={overrides.tools}
                  onChange={(e) => onOverridesChange({ ...overrides, tools: e.target.checked })}
                  className="w-4 h-4"
                  data-testid={`${testId}-tools`}
                />
                <span
                  className="text-xs font-mono"
                  style={{
                    color: 'var(--fg-muted)',
                    fontFamily: 'var(--font-family-mono)',
                  }}
                >
                  Enable tools
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Presets */}
        <div className="mt-4">
          <label
            className="block text-xs font-mono mb-2"
            style={{
              color: 'var(--fg-muted)',
              fontFamily: 'var(--font-family-mono)',
            }}
          >
            Quick Presets
          </label>
          <div className="flex gap-2 flex-wrap">
            {PRESETS.map((preset) => (
              <button
                key={preset.name}
                onClick={() => applyPreset(preset)}
                className="px-3 py-1 text-xs rounded border transition-colors focus:outline-none focus:ring-2"
                style={{
                  backgroundColor: 'var(--bg-surface)',
                  borderColor: 'var(--border-default)',
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
                data-testid={`${testId}-preset-${preset.name.toLowerCase()}`}
              >
                {preset.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Mode Controls */}
      <div>
        <h5
          className="text-sm font-mono font-medium mb-3"
          style={{
            color: 'var(--fg-primary)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          Execution Mode
        </h5>

        <div className="space-y-3">
          {/* Sandbox Mode */}
          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={sandboxMode}
              onChange={handleSandboxToggle}
              className="w-4 h-4"
              data-testid={`${testId}-sandbox`}
            />
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" style={{ color: 'var(--success)' }} />
              <span
                className="text-sm font-mono"
                style={{
                  color: 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Sandbox Mode
              </span>
              <span
                className="text-xs px-2 py-1 rounded"
                style={{
                  backgroundColor: 'var(--success)',
                  color: 'var(--bg-app)',
                }}
              >
                SAFE
              </span>
            </div>
          </label>

          {/* Save to Execution */}
          <label className={cn("flex items-center gap-3", sandboxMode && "opacity-50 cursor-not-allowed")}>
            <input
              type="checkbox"
              checked={saveToExecution}
              onChange={handleSaveToggle}
              disabled={sandboxMode}
              className="w-4 h-4"
              data-testid={`${testId}-save`}
            />
            <div className="flex items-center gap-2">
              <Save className="w-4 h-4" style={{ color: sandboxMode ? 'var(--fg-muted)' : 'var(--warning)' }} />
              <span
                className="text-sm font-mono"
                style={{
                  color: sandboxMode ? 'var(--fg-muted)' : 'var(--fg-primary)',
                  fontFamily: 'var(--font-family-mono)',
                }}
              >
                Save to Execution
              </span>
              {!sandboxMode && saveToExecution && (
                <span
                  className="text-xs px-2 py-1 rounded"
                  style={{
                    backgroundColor: 'var(--warning)',
                    color: 'var(--bg-app)',
                  }}
                >
                  LIVE
                </span>
              )}
            </div>
          </label>

          {!sandboxMode && (
            <div
              className="flex items-start gap-2 p-2 rounded"
              style={{
                backgroundColor: 'var(--warning)',
                color: 'var(--bg-app)',
              }}
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="text-xs">
                <strong>Live Mode:</strong> This will create real entries in the database.
                Enable "Save to Execution" to persist results permanently.
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Run Controls */}
      <div>
        <h5
          className="text-sm font-mono font-medium mb-3"
          style={{
            color: 'var(--fg-primary)',
            fontFamily: 'var(--font-family-mono)',
          }}
        >
          Controls
        </h5>

        <div className="flex items-center gap-3">
          {/* Run Button */}
          <button
            onClick={onRun}
            disabled={isRunning}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2",
              isRunning && "opacity-50 cursor-not-allowed"
            )}
            style={{
              backgroundColor: isRunning ? 'var(--bg-surface)' : 'var(--accent)',
              borderColor: isRunning ? 'var(--border-default)' : 'var(--accent)',
              color: isRunning ? 'var(--fg-muted)' : 'var(--bg-app)',
            }}
            data-testid={`${testId}-run`}
          >
            <Play className="w-4 h-4" />
            <span
              className="font-mono font-medium"
              style={{ fontFamily: 'var(--font-family-mono)' }}
            >
              {isRunning ? 'Running...' : 'Run'}
            </span>
          </button>

          {/* Cancel Button */}
          {isRunning && (
            <button
              onClick={onCancel}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors focus:outline-none focus:ring-2"
              style={{
                backgroundColor: 'var(--danger)',
                borderColor: 'var(--danger)',
                color: 'var(--bg-app)',
              }}
              data-testid={`${testId}-cancel`}
            >
              <Square className="w-4 h-4" />
              <span
                className="font-mono font-medium"
                style={{ fontFamily: 'var(--font-family-mono)' }}
              >
                Cancel
              </span>
            </button>
          )}

          {/* Retry Failed Only (disabled for P1) */}
          <button
            disabled
            className="flex items-center gap-2 px-4 py-2 rounded-lg border opacity-50 cursor-not-allowed"
            style={{
              backgroundColor: 'var(--bg-surface)',
              borderColor: 'var(--border-default)',
              color: 'var(--fg-muted)',
            }}
            title="Retry failed only - Available in future updates"
          >
            <RotateCcw className="w-4 h-4" />
            <span
              className="font-mono font-medium"
              style={{ fontFamily: 'var(--font-family-mono)' }}
            >
              Retry Failed Only
            </span>
          </button>
        </div>

        {/* Status Row */}
        {(isRunning || lastRun) && (
          <div
            className="mt-3 p-3 rounded border"
            style={{
              backgroundColor: 'var(--bg-elevated)',
              borderColor: 'var(--border-default)',
            }}
          >
            <div className="grid grid-cols-4 gap-4 text-xs font-mono">
              <div>
                <span style={{ color: 'var(--fg-muted)' }}>Status:</span>{' '}
                {isRunning ? (
                  <span style={{ color: 'var(--warning)' }}>Running</span>
                ) : lastRun?.success ? (
                  <span style={{ color: 'var(--success)' }}>Success</span>
                ) : (
                  <span style={{ color: 'var(--danger)' }}>Failed</span>
                )}
              </div>

              {lastRun && (
                <>
                  <div>
                    <span style={{ color: 'var(--fg-muted)' }}>Duration:</span>{' '}
                    {formatDuration(lastRun.duration)}
                  </div>
                  <div>
                    <span style={{ color: 'var(--fg-muted)' }}>Tokens:</span>{' '}
                    {lastRun.tokens.input}→{lastRun.tokens.output}
                  </div>
                  <div>
                    <span style={{ color: 'var(--fg-muted)' }}>Cost:</span>{' '}
                    ${lastRun.cost.toFixed(4)}
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}